/**
 * TasteTrace App — Main Application (UI Revolution v4)
 * Clean, minimal, professional
 */
import React, { useState, useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// Data & i18n
import { RESTAURANTS } from './data/restaurants';
import { getTranslation } from './i18n/translations';

// Hooks
import { useLocalStorage, STORAGE_KEYS } from './hooks/useLocalStorage';

// Components
import Header from './components/layout/Header';
import Sidebar from './components/layout/Sidebar';
import DetailPanel from './components/layout/DetailPanel';
import { MapController, createRestaurantIcon } from './components/map/MapController';
import { Modal, BadgeGuide } from './components/shared';
import { FeedbackForm } from './components/features/community';
import { MessageSquare, Heart } from 'lucide-react';

// Feature Modals
import DishScanner from './components/features/dish/DishScanner';
import FoodieMatch from './components/features/match/FoodieMatch';
import MenuDecoder from './components/features/menu/MenuDecoder';
import RoutePlanner from './components/features/route/RoutePlanner';

// Fix Leaflet default marker icons
import L from 'leaflet';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

export default function App() {
  // State
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [filter, setFilter] = useState('all');
  const [dietaryFilter, setDietaryFilter] = useState(null);
  const [language, setLanguage] = useLocalStorage(STORAGE_KEYS.language, 'th');
  const [currency, setCurrency] = useLocalStorage('tastetrace_currency', 'thb');
  const [searchQuery, setSearchQuery] = useState('');
  const [copied, setCopied] = useState(false);

  // Feature modals
  const [activeFeature, setActiveFeature] = useState(null);
  const [fabOpen, setFabOpen] = useState(false);

  // User data (persisted)
  const [favorites, setFavorites] = useLocalStorage(STORAGE_KEYS.favorites, []);
  const [stats, setStats] = useLocalStorage(STORAGE_KEYS.stats, { viewed: [], shared: 0 });

  // Translation helper
  const t = getTranslation(language);

  // Badge Guide Modal
  const [showBadgeGuide, setShowBadgeGuide] = useState(false);

  // Community Modal
  const [showCommunityModal, setShowCommunityModal] = useState(false);
  const [communityPosts, setCommunityPosts] = useLocalStorage('tastetrace_community_posts', []);

  const handleCommunitySubmit = (post) => {
    setCommunityPosts(prev => [{ ...post, id: Date.now() }, ...prev]);
    // Optional: Keep modal open or close it. Passing simple success message could be good.
    // For now, let's keep it open to show the new post, or close it if it's just feedback.
    // Let's close it for simplicity as per standard feedback forms.
    setShowCommunityModal(false);
  };

  // Filter restaurants
  const filteredRestaurants = useMemo(() => {
    let results = RESTAURANTS;

    if (filter !== 'all') {
      if (filter === 'favorites') {
        results = results.filter(r => favorites.includes(r.id));
      } else {
        results = results.filter(r => r.badges?.includes(filter));
      }
    }

    if (dietaryFilter) {
      results = results.filter(r => r.dietary?.includes(dietaryFilter));
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      results = results.filter(r =>
        r.name.toLowerCase().includes(query) ||
        r.category.toLowerCase().includes(query)
      );
    }

    return results;
  }, [filter, dietaryFilter, searchQuery, favorites]);

  // Social sharing
  const handleShare = async (restaurant) => {
    const url = `${window.location.origin}?restaurant=${restaurant.id}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: restaurant.name,
          text: `Check out ${restaurant.name} on TasteTrace!`,
          url: url
        });
      } catch {
        // User cancelled
      }
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }

    setStats({ ...stats, shared: stats.shared + 1 });
  };

  // Toggle favorite
  const toggleFavorite = (restaurantId) => {
    const newFavorites = favorites.includes(restaurantId)
      ? favorites.filter(id => id !== restaurantId)
      : [...favorites, restaurantId];
    setFavorites(newFavorites);
  };

  // Track restaurant view
  useEffect(() => {
    if (selectedRestaurant && !stats.viewed.includes(selectedRestaurant.id)) {
      const newViewed = [...stats.viewed, selectedRestaurant.id];
      setStats({ ...stats, viewed: newViewed });
    }
  }, [selectedRestaurant]);

  // Close FAB when clicking outside
  useEffect(() => {
    if (fabOpen) {
      const handleClick = () => setFabOpen(false);
      setTimeout(() => document.addEventListener('click', handleClick), 0);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [fabOpen]);

  return (
    <div className="relative w-screen h-screen overflow-hidden bg-neutral-100 font-sans text-neutral-800">
      {/* Main Content - Full Screen Map */}
      <MapContainer
        center={[13.7563, 100.5018]}
        zoom={13}
        className="absolute inset-0 z-0 h-full w-full"
        zoomControl={false} // Will add custom positioned zoom control if needed, or rely on Leaflet defaults
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />
        <MapController selectedRestaurant={selectedRestaurant} />

        {filteredRestaurants.map(r => (
          <Marker
            key={r.id}
            position={r.coordinates}
            icon={createRestaurantIcon(r)}
            eventHandlers={{ click: () => setSelectedRestaurant(r) }}
          />
        ))}
      </MapContainer>

      {/* Floating Sidebar (Left Panel) */}
      <Sidebar
        restaurants={filteredRestaurants}
        selectedRestaurant={selectedRestaurant}
        onSelectRestaurant={setSelectedRestaurant}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        filter={filter}
        onFilterChange={setFilter}
        dietaryFilter={dietaryFilter}
        onDietaryFilterChange={setDietaryFilter}
        favorites={favorites}
        t={t}
        onBadgeClick={() => setShowBadgeGuide(true)}
        onCommunityClick={() => setShowCommunityModal(true)}
      >
        {/* Header inside sidebar */}
        <Header
          language={language}
          onLanguageChange={setLanguage}
          currency={currency}
          onCurrencyChange={setCurrency}
          t={t}
          onCommunityClick={() => setShowCommunityModal(true)}
        />
      </Sidebar>

      {/* Detail Panel */}
      <DetailPanel
        restaurant={selectedRestaurant}
        onClose={() => setSelectedRestaurant(null)}
        onToggleFavorite={() => toggleFavorite(selectedRestaurant?.id)}
        onShare={() => handleShare(selectedRestaurant)}
        isFavorite={favorites.includes(selectedRestaurant?.id)}
        copied={copied}
        currency={currency}
        t={t}
        language={language}
        onBadgeClick={() => setShowBadgeGuide(true)}
        onMenuDecode={() => setActiveFeature('menuDecode')}
      />

      {/* Floating Action Button (FAB) — Premium features menu */}
      <div className="absolute bottom-6 right-6 z-40 flex flex-col items-end gap-3 pointer-events-none">
        <div className="pointer-events-auto">
          {/* ... FAB Menu (expanded) ... */}
          {fabOpen && (
            <div className="absolute bottom-16 right-0 bg-white/90 backdrop-blur-xl rounded-3xl shadow-spring border border-white/60 overflow-hidden animate-scaleIn origin-bottom-right min-w-[200px]">
              <button
                onClick={() => { setActiveFeature('dishScan'); setFabOpen(false); }}
                className="w-full flex items-center gap-3.5 px-5 py-3.5 text-[13.5px] font-medium text-slate-700 hover:bg-slate-50 hover:text-coral-500 transition-colors duration-400 ease-spring text-left"
              >
                <span className="text-base">📸</span>
                <span>Dish Scanner AI</span>
              </button>
              <div className="h-px bg-slate-100/80" />
              <button
                onClick={() => { setActiveFeature('menuDecode'); setFabOpen(false); }}
                className="w-full flex items-center gap-3.5 px-5 py-3.5 text-[13.5px] font-medium text-slate-700 hover:bg-slate-50 hover:text-coral-500 transition-colors duration-400 ease-spring text-left"
              >
                <span className="text-base">💬</span>
                <span>Menu Decoder AI</span>
              </button>
              <div className="h-px bg-slate-100/80" />
              <button
                onClick={() => { setActiveFeature('foodieMatch'); setFabOpen(false); }}
                className="w-full flex items-center gap-3.5 px-5 py-3.5 text-[13.5px] font-medium text-slate-700 hover:bg-slate-50 hover:text-coral-500 transition-colors duration-400 ease-spring text-left"
              >
                <span className="text-base">🤝</span>
                <span>{t('findPartners')}</span>
              </button>
              <div className="h-px bg-slate-100/80" />
              <button
                onClick={() => { setActiveFeature('routePlanner'); setFabOpen(false); }}
                className="w-full flex items-center gap-3.5 px-5 py-3.5 text-[13.5px] font-medium text-slate-700 hover:bg-slate-50 hover:text-coral-500 transition-colors duration-400 ease-spring text-left"
              >
                <span className="text-base">🗺️</span>
                <span>{t('planTrip')}</span>
              </button>
            </div>
          )}

          {/* FAB Button */}
          <button
            onClick={(e) => { e.stopPropagation(); setFabOpen(!fabOpen); }}
            className={`w-14 h-14 rounded-[22px] shadow-spring flex items-center justify-center text-white transition-all duration-400 ease-spring active:scale-90 ${fabOpen
              ? 'bg-slate-800 rotate-45'
              : 'bg-emerald-500 hover:bg-emerald-600 hover:-translate-y-1 hover:shadow-lg'
              }`}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>

        {/* Feature Modals */}
        <DishScanner
          isOpen={activeFeature === 'dishScan'}
          onClose={() => setActiveFeature(null)}
          onRestaurantSelect={setSelectedRestaurant}
          t={t}
        />

        <FoodieMatch
          isOpen={activeFeature === 'foodieMatch'}
          onClose={() => setActiveFeature(null)}
          restaurant={selectedRestaurant}
          t={t}
        />

        <MenuDecoder
          isOpen={activeFeature === 'menuDecode'}
          onClose={() => setActiveFeature(null)}
          restaurant={selectedRestaurant}
          dishes={selectedRestaurant?.dishes}
          t={t}
        />

        <RoutePlanner
          isOpen={activeFeature === 'routePlanner'}
          onClose={() => setActiveFeature(null)}
          restaurants={RESTAURANTS}
          t={t}
        />

        <BadgeGuide
          isOpen={showBadgeGuide}
          onClose={() => setShowBadgeGuide(false)}
          t={t}
          language={language}
        />

        {/* Community Modal */}
        <Modal
          isOpen={showCommunityModal}
          onClose={() => setShowCommunityModal(false)}
          title={language === 'th' ? 'ชุมชน & ข้อเสนอแนะ' : 'Community & Feedback'}
          icon={<MessageSquare size={20} />}
        >
          <div className="max-h-[60vh] overflow-y-auto pr-1 mb-6 space-y-4">
            {communityPosts.length > 0 ? (
              communityPosts.map(post => (
                <div key={post.id} className="bg-white p-4 rounded-xl border border-neutral-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-brand-50 text-brand-700 flex items-center justify-center font-bold text-[13px] uppercase">
                        {post.author ? post.author[0] : 'U'}
                      </div>
                      <div>
                        <div className="text-[13px] font-semibold text-neutral-800">{post.author || 'Anonymous User'}</div>
                        <div className="text-[10px] text-neutral-400">{new Date(post.createdAt).toLocaleDateString()}</div>
                      </div>
                    </div>
                    <span className="text-[10px] font-bold text-brand-600 uppercase tracking-wider px-2 py-0.5 bg-brand-50 rounded-full">{post.category}</span>
                  </div>
                  <p className="text-[13.5px] text-neutral-700 leading-relaxed">{post.content}</p>
                  <div className="flex items-center gap-4 mt-3 pt-3 border-t border-neutral-100/80">
                    <button className="flex items-center gap-1.5 text-[11px] font-medium text-neutral-500 hover:text-brand-600 transition-colors group">
                      <Heart size={14} className="group-hover:fill-brand-100 transition-colors" />
                      <span>{Math.floor(Math.random() * 10) + 1}</span>
                    </button>
                    <button className="flex items-center gap-1.5 text-[11px] font-medium text-neutral-500 hover:text-brand-600 transition-colors group">
                      <MessageSquare size={14} className="group-hover:text-brand-500 transition-colors" />
                      <span>{language === 'th' ? 'ตอบกลับ' : 'Reply'}</span>
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-neutral-400 text-sm italic">
                {language === 'th' ? 'ยังไม่มีความคิดเห็น เริ่มต้นได้เลย!' : 'No posts yet. Be the first!'}
              </div>
            )}
          </div>
          <div className="border-t border-neutral-100 pt-4">
            <h4 className="text-sm font-semibold mb-3">{language === 'th' ? 'ความคิดเห็นใหม่' : 'New Post'}</h4>
            <FeedbackForm
              onSubmit={handleCommunitySubmit}
              onCancel={() => setShowCommunityModal(false)}
              language={language}
            />
          </div>
        </Modal>
      </div>

    </div>
  );
}
